import { exportToHTML } from './exportUtils';

// Types
interface ServiceStatus {
  name: string;
  status: 'operational' | 'degraded' | 'down';
  message: string;
  lastChecked: Date;
  responseTime?: number;
}

interface CloudflareCredentials {
  accountEmail: string;
  apiKey: string;
  zoneId: string;
  r2BucketName: string;
  accountId: string;
}

interface HTMLCSStoImageCredentials {
  userId: string;
  apiKey: string;
}

// Mock data for development
const MOCK_CREDENTIALS = {
  userId: '44be8eac-3bdb-4c64-aa86-675a75e6d3d9',
  apiKey: 'd8985858-4e6a-46de-bcde-a443cc6cb4b6'
};

const MOCK_CLOUDFLARE = {
  accountEmail: 'support@dealercentive.com',
  apiKey: '7aa03210fd5b9a2f9f033d3ecc63f5033d50b',
  zoneId: '0083446677b3cbc6207ee6db04b0599e',
  accountId: 'beb860d7a22688e729c77f7e8feab88a',
  r2BucketName: 'dealercentive-banners'
};

// HTMLCSStoImage API Functions
export const getHTMLCSStoImageCredentials = (): HTMLCSStoImageCredentials => {
  const stored = localStorage.getItem('htmlcsstoimage_credentials');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return MOCK_CREDENTIALS;
    }
  }
  return MOCK_CREDENTIALS;
};

export const updateHTMLCSStoImageCredentials = (userId: string, apiKey: string): boolean => {
  try {
    localStorage.setItem('htmlcsstoimage_credentials', JSON.stringify({ userId, apiKey }));
    return true;
  } catch {
    return false;
  }
};

export const resetHTMLCSStoImageCredentials = (): void => {
  localStorage.removeItem('htmlcsstoimage_credentials');
};

export const testHTMLCSStoImageAPI = async (): Promise<{ success: boolean; error?: string; responseTime?: number }> => {
  try {
    const credentials = getHTMLCSStoImageCredentials();
    console.log('Testing HTMLCSStoImage API with credentials:', { userId: credentials.userId });
    
    const start = Date.now();
    const response = await fetch('https://images.dealercentive.us/api/test/htmlcsstoimage', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: credentials.userId,
        apiKey: credentials.apiKey
      })
    });

    const responseData = await response.json();
    console.log('HTMLCSStoImage API Response:', responseData);

    if (!response.ok) {
      throw new Error(`API returned ${response.status}: ${responseData.message || 'Unknown error'}`);
    }

    const end = Date.now();
    const responseTime = end - start;
    return { success: true, responseTime };
  } catch (error) {
    console.error('HTMLCSStoImage API Test Error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'API test failed' 
    };
  }
};

// Cloudflare Functions
export const getCloudflareCredentials = (): CloudflareCredentials => {
  const stored = localStorage.getItem('cloudflare_credentials');
  if (stored) {
    try {
      const credentials = JSON.parse(stored);
      if (credentials.accountId && credentials.apiKey && credentials.accountEmail) {
        console.log('Using stored Cloudflare credentials:', {
          accountId: credentials.accountId,
          accountEmail: credentials.accountEmail,
          r2BucketName: credentials.r2BucketName,
          isApiToken: credentials.apiKey.startsWith('v1.') || credentials.apiKey.startsWith('-')
        });
        return credentials;
      } else {
        console.warn('Stored Cloudflare credentials missing required fields:', {
          hasAccountId: !!credentials.accountId,
          hasApiKey: !!credentials.apiKey,
          hasAccountEmail: !!credentials.accountEmail,
          hasR2BucketName: !!credentials.r2BucketName
        });
      }
    } catch (error) {
      console.error('Error parsing stored Cloudflare credentials:', error);
    }
  }
  
  // Return default credentials if stored ones are invalid
  const defaultCredentials = {
    accountId: 'beb860d7a22688e729c77f7e8feab88a',
    apiKey: '-P4_62kUGMdS5-jHK9csV2vG37S2AI2hYig4gvMt', // Cloudflare API Token
    accountEmail: 'support@dealercentive.com',
    zoneId: '0083446677b3cbc6207ee6db04b0599e',
    r2BucketName: 'dealercentive-banners'
  };

  // Store the default credentials
  try {
    localStorage.setItem('cloudflare_credentials', JSON.stringify(defaultCredentials));
  } catch (error) {
    console.error('Failed to store default Cloudflare credentials:', error);
  }

  console.log('Using default Cloudflare credentials');
  return defaultCredentials;
};

export const updateCloudflareCredentials = (credentials: CloudflareCredentials): boolean => {
  try {
    localStorage.setItem('cloudflare_credentials', JSON.stringify(credentials));
    return true;
  } catch {
    return false;
  }
};

export const testCloudflareR2 = async (): Promise<{ success: boolean; message: string; responseTime?: number }> => {
  try {
    const credentials = getCloudflareCredentials();
    
    // Validate required fields
    if (!credentials.accountId || !credentials.apiKey || !credentials.r2BucketName) {
      console.error('Missing required Cloudflare credentials:', {
        hasAccountId: !!credentials.accountId,
        hasApiKey: !!credentials.apiKey,
        hasBucketName: !!credentials.r2BucketName
      });
      return { 
        success: false, 
        message: 'Missing required Cloudflare credentials. Please check your settings.' 
      };
    }

    const start = Date.now();
    const requestData = {
      accountId: credentials.accountId,
      apiKey: credentials.apiKey,
      bucketName: credentials.r2BucketName,
      isApiToken: credentials.apiKey.startsWith('v1.') || credentials.apiKey.startsWith('-')
    };

    console.log('Testing Cloudflare R2 with credentials:', {
      accountId: requestData.accountId,
      bucketName: requestData.bucketName,
      isApiToken: requestData.isApiToken
    });
    
    const response = await fetch('https://images.dealercentive.us/api/test/r2', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });

    const responseData = await response.json();
    console.log('Cloudflare R2 Response:', responseData);

    if (!response.ok) {
      throw new Error(`R2 API returned ${response.status}: ${responseData.message || 'Unknown error'}`);
    }

    const end = Date.now();
    const responseTime = end - start;
    return { success: true, message: 'R2 bucket is accessible', responseTime };
  } catch (error) {
    console.error('Cloudflare R2 Test Error:', error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Failed to access R2 bucket' 
    };
  }
};

export const testCloudflareWorker = async (): Promise<{ success: boolean; message: string; responseTime?: number }> => {
  try {
    console.log('Testing Cloudflare Worker...');
    
    const start = Date.now();
    const response = await fetch('https://images.dealercentive.us/api/test/worker', {
      method: 'GET'
    });

    const responseData = await response.json();
    console.log('Cloudflare Worker Response:', responseData);

    if (!response.ok) {
      throw new Error(`Worker returned ${response.status}: ${responseData.message || 'Unknown error'}`);
    }

    const end = Date.now();
    const responseTime = end - start;
    return { success: true, message: 'Worker is operational', responseTime };
  } catch (error) {
    console.error('Cloudflare Worker Test Error:', error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Worker test failed' 
    };
  }
};

// Production Health Check
export const checkProductionHealth = async (): Promise<ServiceStatus[]> => {
  const statuses: ServiceStatus[] = [];
  const now = new Date();

  try {
    // Test HTMLCSStoImage API
    const htmlCssTest = await testHTMLCSStoImageAPI();
    statuses.push({
      name: 'HTMLCSStoImage API',
      status: htmlCssTest.success ? 'operational' : 'down',
      message: htmlCssTest.success ? 'API service is operational' : htmlCssTest.error || 'API test failed',
      lastChecked: now,
      responseTime: htmlCssTest.responseTime
    });

    // Test Cloudflare R2
    const r2Test = await testCloudflareR2();
    statuses.push({
      name: 'Cloudflare R2',
      status: r2Test.success ? 'operational' : 'down',
      message: r2Test.message,
      lastChecked: now,
      responseTime: r2Test.responseTime
    });

    // Test Cloudflare Worker
    const workerTest = await testCloudflareWorker();
    statuses.push({
      name: 'Cloudflare Worker',
      status: workerTest.success ? 'operational' : 'down',
      message: workerTest.message,
      lastChecked: now,
      responseTime: workerTest.responseTime
    });

    return statuses;
  } catch (error) {
    console.error('Production health check failed:', error);
    return [{
      name: 'System Health',
      status: 'down',
      message: error instanceof Error ? error.message : 'Unable to perform health check',
      lastChecked: now
    }];
  }
};

// Test File Creation
export const createTestHTMLFile = (clientId: string, category: string, size: string, index: number): void => {
  const testHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Test Banner</title>
      <style>
        body { margin: 0; padding: 0; }
        .banner { width: 100%; height: 100vh; background: #f0f0f0; }
      </style>
    </head>
    <body>
      <div class="banner">
        <h1>Test Banner</h1>
        <p>Client: ${clientId}</p>
        <p>Category: ${category}</p>
        <p>Size: ${size}</p>
        <p>Index: ${index}</p>
      </div>
    </body>
    </html>
  `;

  const blob = new Blob([testHtml], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${clientId}_${category}_${size}-${index}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Utility functions for deployment
const generateDeploymentId = (): string => {
  return `deploy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const addProductionMetadata = (htmlContent: string, metadata: {
  clientId: string;
  vehicleType: string;
  priority: number;
  deploymentId: string;
  refreshInterval: number;
  lastUpdated: string;
}): string => {
  const metadataScript = `
    <script>
      window.__BANNER_METADATA__ = ${JSON.stringify(metadata)};
    </script>
  `;
  return htmlContent.replace('</body>', `${metadataScript}</body>`);
};

const generateHtmlKey = (clientId: string, vehicleType: string, sizeName: string, priority: number): string => {
  const sanitizedClientId = clientId.toLowerCase().replace(/[^a-z0-9]/g, '');
  const sanitizedSize = sizeName.toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${sanitizedClientId}/${vehicleType}/${sanitizedSize}-${priority}.html`;
};

const deployHtmlToR2 = async (htmlContent: string, key: string): Promise<string> => {
  try {
    const credentials = getCloudflareCredentials();
    const response = await fetch('https://images.dealercentive.us/api/deploy/html', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        content: htmlContent,
        key,
        credentials: {
          accountId: credentials.accountId,
          apiKey: credentials.apiKey,
          bucketName: credentials.r2BucketName
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to deploy HTML: ${response.statusText}`);
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('HTML deployment failed:', error);
    throw error;
  }
};

// --- Real Deployment Logic (simplified for brevity) ---
export const deployBannerToProduction = async (
  elements: any[],
  size: any,
  dataFeedArray: any[],
  config: any,
  clientId: string
): Promise<{ success: boolean; htmlUrl: string; imageUrl: string; deploymentId: string; error?: string }> => {
  try {
    const deploymentId = generateDeploymentId();
    const primaryDataFeed = dataFeedArray[0];
    const htmlContent = `<div>Test HTML Content</div>`; // Simplified for testing
    const enhancedHTML = addProductionMetadata(htmlContent, {
      clientId,
      vehicleType: config.vehicleType,
      priority: config.priority,
      deploymentId,
      refreshInterval: config.refreshInterval,
      lastUpdated: new Date().toISOString()
    });
    const htmlKey = generateHtmlKey(clientId, config.vehicleType, size.name, config.priority);
    const htmlUrl = await deployHtmlToR2(enhancedHTML, htmlKey);
    const imageUrl = '';

    return {
      success: true,
      htmlUrl,
      imageUrl,
      deploymentId
    };
  } catch (error) {
    console.error('Production deployment failed:', error);
    return {
      success: false,
      htmlUrl: '',
      imageUrl: '',
      deploymentId: '',
      error: error instanceof Error ? error.message : 'Deployment failed'
    };
  }
};

export const checkProductionServices = async () => {
  // ... logic to check all production services ...
  return [];
};

export const batchDeployToProduction = async (deployments: any[]): Promise<Array<{ success: boolean; sizeId: string; error?: string; imageUrl?: string }>> => {
  try {
    const results = await Promise.all(
      deployments.map(async (deployment) => {
        try {
          const result = await deployBannerToProduction(
            deployment.elements,
            deployment.size,
            deployment.dataFeedArray,
            deployment.config,
            deployment.clientId
          );

          return {
            success: result.success,
            sizeId: deployment.size.id,
            imageUrl: result.imageUrl,
            error: result.error
          };
        } catch (error) {
          console.error(`Deployment failed for size ${deployment.size.id}:`, error);
          return {
            success: false,
            sizeId: deployment.size.id,
            error: error instanceof Error ? error.message : 'Deployment failed'
          };
        }
      })
    );

    return results;
  } catch (error) {
    console.error('Batch deployment failed:', error);
    throw new Error(error instanceof Error ? error.message : 'Batch deployment failed');
  }
};
